import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { UUID } from "@/Shared/types";
import type { ReactNode } from "react";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useBrokersModifyBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersModifyBrokerDialog";
import { BookShipmentChangeBrokerDialog } from "../Dialogs/BookShipmentChangeBrokerDialog";

interface ChangeBrokerState {
  savedBrokerId: UUID;
  savedBrokers: Array<SavedBroker>;
  onBrokerChanged: (updatedBroker: Broker) => void;
}

// Declarative replacement for the imperative useBookShipmentChangeBrokerDialog. Two
// situations:
// 1) Only one saved broker (or the shipment's broker matches none of them):
//    jump straight to the modify wizard.
// 2) Multiple saved brokers: show the richer select/modify/add dialog.
export function useBookShipmentChangeBrokerDialog(): {
  show: (
    selectedSavedBrokerId: string | undefined,
    currentBroker: Broker,
    savedBrokers: Array<SavedBroker>,
    onBrokerChanged: (updatedBroker: Broker) => void,
  ) => Promise<void>;
  dialogs: ReactNode;
} {
  const modifyBrokerDialog = useBrokersModifyBrokerDialog();
  const [openCount, setOpenCount] = useState(0);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeState, setChangeState] = useState<ChangeBrokerState | undefined>(
    undefined,
  );

  async function show(
    selectedSavedBrokerId: string | undefined,
    currentBroker: Broker,
    savedBrokers: Array<SavedBroker>,
    onBrokerChanged: (updatedBroker: Broker) => void,
  ) {
    if (savedBrokers.length <= 1 || selectedSavedBrokerId === undefined) {
      const savedBroker = savedBrokers.find(function (sb) {
        return sb.savedBrokerId === selectedSavedBrokerId;
      }) ?? { savedBrokerId: uuidv4(), broker: currentBroker };
      const result = await modifyBrokerDialog.show(savedBroker);
      if (result !== undefined) {
        onBrokerChanged(result.broker);
      }
    } else {
      setChangeState({
        savedBrokerId: selectedSavedBrokerId,
        savedBrokers,
        onBrokerChanged,
      });
      setOpenCount((count) => count + 1);
      setChangeOpen(true);
    }
  }

  const dialogs = (
    <>
      {modifyBrokerDialog.dialogs}
      {changeState !== undefined && (
        <BookShipmentChangeBrokerDialog
          key={`book-shipment-change-broker-${openCount}`}
          open={changeOpen}
          onOpenChange={setChangeOpen}
          savedBrokerId={changeState.savedBrokerId}
          savedBrokers={changeState.savedBrokers}
          onBrokerChanged={changeState.onBrokerChanged}
        />
      )}
    </>
  );

  return { show, dialogs };
}
